import type { HTMLAttributes } from 'react'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Copy, Delete, Move, Rename, RestoreFromTrash, RestorePage, Settings, Share } from '@tunasong/icons'
import { logger } from '@tunasong/models'
import {
  getActionsComponent,
  getPlugin,
  useEntityCommands,
  useGlobalAppContext,
  usePlugins,
  type Command,
} from '@tunasong/plugin-lib'
import { features, useThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isFolderContainer, isVersioned } from '@tunasong/schemas'
import {ConfirmDialog, Dialog, Divider, EntityCard, FavoriteMenuItem, getEntityPath, useAlert, useIsOwner, Button, MenuItem, ListItemIcon, ListItemText, DialogActions, DialogContent, TextField} from '@tunasong/ui-lib'
import type { MouseEvent } from 'react'
import React, { useState } from 'react'
import invariant from 'tiny-invariant'
import { EntitySelectDialog } from '../entity/entity-select-dialog.js'
import EntityHistory from '../entity/history.js'
import { useShare } from '../share/share.js'

export interface EntityMenuItemsProps extends HTMLAttributes<HTMLDivElement> {
  entity?: Persisted<Entity>
  /** Close the menu, but keep the dialogs open */
  onCloseMenu(): void
  /** Close everything */
  onClose(): void
}

/** @todo this is slowing down long lists */
export const useEntityMenuItems = ({ entity, onClose, onCloseMenu }: EntityMenuItemsProps) => {
  const dispatch = useThunkDispatch()
  const plugins = usePlugins('all')
  const { moveEntity } = graphHooks.useEntityUtil()
  const updateEntity = graphHooks.useEntityUpdate({ debounceDelay: 0 })
  const { cloneEntity } = graphHooks.useEntityClone()

  // get the entity commands
  const commands = useEntityCommands(entity)

  const { dialog: shareDialog, show: showShare } = useShare(entity, onClose)

  const handleShare = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    showShare()
    onCloseMenu()
  }

  const isOwner = useIsOwner(entity)

  const [showMoveDialog, setShowMoveDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleShowMoveDialog = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowMoveDialog(true)
    onCloseMenu()
  }

  const handleCopy = (entity: Persisted<Entity>) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    void cloneEntity({ entity, updatedProps: { name: `Copy of ${entity.name}` } }).then(() => {
      onClose()
    })
  }

  const trash = graphHooks.useEntityTrash(entity)

  const handleTrash = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    trash()
    onClose()
  }

  const handleRestore = (entity: Persisted<Entity>) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    updateEntity(entity.id, { trash: false })
    onClose()
  }

  const handleShowDelete = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(true)
    onCloseMenu()
  }

  const handleHideDelete = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    onClose()
  }

  /** Rename */
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const handleShowRenameDialog = (open: boolean) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowRenameDialog(open)
    if (open) {
      onCloseMenu()
    } else {
      onClose()
    }
  }
  const [name, setName] = useState<string>()
  const handleRenameChange = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)
  const handleRename = (entity: Persisted<Entity>) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    updateEntity(entity.id, { name })
    setShowRenameDialog(false)
    setName(undefined)
    onClose()
  }
  const [showHistory, setShowHistory] = useState(false)
  const handleShowHistory = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowHistory(true)
    onCloseMenu()
  }

  const handleHideHistory = () => {
    setShowHistory(false)
    onClose()
  }

  const [showProperties, setShowProperties] = useState(false)
  const handleShowProperties = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    setShowProperties(true)
    onCloseMenu()
  }
  const onCloseProperties = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    setShowProperties(false)
    onClose()
  }

  const handleProperties = (entity: Persisted<Entity>) => {
    updateEntity(entity.id, entity)
  }

  const handleMoveSelect = (entity: Persisted<Entity>) => (selected: Persisted<Entity> | undefined) => {
    setShowMoveDialog(false)
    onClose()

    if (!selected) {
      return
    }

    /** Move the entity */
    /** @todo handle ACLs */
    void moveEntity({ entity, newParent: selected })
    dispatch(
      features.notifications.actions.setAlert({
        title: 'Entity moved',
        severity: 'success',
        message: `${entity.name} moved to ${selected.name}`,
        link: getEntityPath({ entity: selected }),
      })
    )
  }

  const plugin = entity ? getPlugin(entity, plugins) : null

  const PluginBlockActions = getActionsComponent(plugins)

  const excludeIds = entity ? ([entity.id, entity.parentId].filter(Boolean) as string[]) : []

  const { prompt } = useGlobalAppContext()
  const { alert } = useAlert()

  const handleCommand = (command: Command) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    const { cmd, cmdParamsSchema, name } = command
    invariant(cmd, 'No command found')

    const execute = async () => {
      const params = cmdParamsSchema ? await prompt({ schema: cmdParamsSchema, title: 'Command parameters' }) : null

      onClose()

      try {
        await cmd(params)
      } catch (err) {
        logger.error('Error executing command', { name, err })
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err && 'data' in err
              ? JSON.stringify(err.data)
              : 'Unknown error'
        alert({
          severity: 'error',
          entity,
          title: `Error executing command: ${name}`,
          message,
          duration: 5000,
        })
      }
    }
    void execute()
  }

  return {
    dialogs: [
      shareDialog,
      ...(entity
        ? [
            <Dialog
              key={12}
              open={showProperties}
              title="Properties"
              onClose={onCloseProperties}
              onClick={ev => ev.stopPropagation()}
            >
              {plugin?.components?.Properties ? (
                <plugin.components.Properties element={entity} onChange={handleProperties} />
              ) : null}
            </Dialog>,
            entity ? (
              <Dialog
                key={13}
                open={showHistory}
                title="History"
                onClose={handleHideHistory}
                onClick={ev => ev.stopPropagation()}
              >
                <EntityHistory entity={entity} onComplete={handleHideHistory} />
              </Dialog>
            ) : null,
            entity ? (
              <EntitySelectDialog
                key={14}
                open={showMoveDialog}
                filter={isFolderContainer}
                title="Select folder"
                onClose={handleMoveSelect(entity)}
                excludeIds={excludeIds}
              />
            ) : null,
            <ConfirmDialog
              key={15}
              open={showDeleteDialog}
              text={`Really move ${entity.type}: ${entity.name} to the trash?`}
              onCancel={handleHideDelete}
              onConfirm={handleTrash}
            >
              <EntityCard style={{ marginTop: 16 }} entity={entity} showMenu={false} headerAction={null} />
            </ConfirmDialog>,
            <Dialog key={16} open={showRenameDialog} title={`Rename ${entity.type}`} onClose={handleShowRenameDialog(false)}>
              <DialogContent>
                <TextField fullWidth autoFocus defaultValue={entity.name} onChange={handleRenameChange} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleRename(entity)} disabled={!name}>
                  Rename
                </Button>
              </DialogActions>
            </Dialog>,
          ]
        : []),
    ],
    menuItems: entity
      ? [
          <FavoriteMenuItem key={1} onClick={onClose} entity={entity} />,
          <Divider key={2} />,

          /** @deprecated - move to entity commands instead */
          <PluginBlockActions key={122} onClose={onClose} element={entity} />,

          // Entity commands
          // @todo entity commands with parameters
          ...commands.map(command => (
            <MenuItem key={command.id} disabled={!command.cmd} onClick={handleCommand(command)}>
              {command.Icon ? (
                <ListItemIcon>
                  <command.Icon />
                </ListItemIcon>
              ) : null}
              <ListItemText primary={command.name} />
            </MenuItem>
          )),

          <MenuItem key={4} onClick={handleShowMoveDialog} disabled={entity.trash}>
            <ListItemIcon>
              <Move />
            </ListItemIcon>
            <ListItemText primary="Move..." />
          </MenuItem>,
          <MenuItem key={5} onClick={handleShowRenameDialog(true)} disabled={entity.trash}>
            <ListItemIcon>
              <Rename />
            </ListItemIcon>
            <ListItemText primary="Rename..." />
          </MenuItem>,
          <MenuItem key={6} onClick={handleCopy(entity)} disabled={entity.trash}>
            <ListItemIcon>
              <Copy />
            </ListItemIcon>
            <ListItemText primary="Copy" />
          </MenuItem>,
          <MenuItem key={7} onClick={handleShare} disabled={entity.trash}>
            <ListItemIcon>
              <Share />
            </ListItemIcon>
            <ListItemText primary="Share" />
          </MenuItem>,
          plugin?.components?.Properties ? (
            <MenuItem key={8} onClick={handleShowProperties}>
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText primary="Properties" />
            </MenuItem>
          ) : null,

          isVersioned(entity) ? (
            <MenuItem key={9} onClick={handleShowHistory} disabled={!isOwner}>
              <ListItemIcon>
                <RestorePage />
              </ListItemIcon>
              <ListItemText primary={`Version History`} />
            </MenuItem>
          ) : null,

          !entity.trash ? (
            <MenuItem key={10} onClick={handleShowDelete} disabled={!isOwner}>
              <ListItemIcon>
                <Delete color="var(--tuna-palette-error-main)" />
              </ListItemIcon>
              <ListItemText primary={'Move to Trash'} />
            </MenuItem>
          ) : null,

          entity.trash ? (
            <MenuItem key={11} onClick={handleRestore(entity)} disabled={!isOwner}>
              <ListItemIcon>
                <RestoreFromTrash />
              </ListItemIcon>
              <ListItemText primary={'Restore from Trash'} />
            </MenuItem>
          ) : null,
        ].filter(Boolean)
      : [],
  }
}
